const CHAT_SESSIONS_STORAGE_PREFIX = 'qmetryAiChatSessions';
const ACTIVE_CHAT_SESSION_STORAGE_PREFIX = 'qmetryAiActiveChatSessionId';
const CHAT_USER_SCOPE_STORAGE_KEY = 'qmetryAiChatUserScope';
const AI_BROWSER_SESSION_STORAGE_KEY = 'qmetrix-ai-browser-session-id';
const DASHBOARD_SESSION_STORAGE_KEY = 'aiDashboardSessionId';

const canUseLocalStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);
const canUseSessionStorage = () => typeof window !== 'undefined' && Boolean(window.sessionStorage);

const normalizeScopePart = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '') || 'anonymous';

const readStoredUserData = () => {
  if (!canUseSessionStorage()) {
    return null;
  }

  try {
    const rawUserData = window.sessionStorage.getItem('userData');
    return rawUserData ? JSON.parse(rawUserData) : null;
  } catch (error) {
    return null;
  }
};

export const getCurrentChatUserScope = () => {
  if (!canUseSessionStorage()) {
    return 'anonymous';
  }

  try {
    const storedScope = window.sessionStorage.getItem(CHAT_USER_SCOPE_STORAGE_KEY);
    if (storedScope) {
      return storedScope;
    }
  } catch (error) {
    // Ignore storage failures and fall back to a derived scope.
  }

  const userData = readStoredUserData();
  const scope = userData?.email || userData?.name || window.sessionStorage.getItem('companyId');
  return normalizeScopePart(scope);
};

export const setCurrentChatUserScope = (scope) => {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    if (scope) {
      window.sessionStorage.setItem(CHAT_USER_SCOPE_STORAGE_KEY, normalizeScopePart(scope));
    } else {
      window.sessionStorage.removeItem(CHAT_USER_SCOPE_STORAGE_KEY);
    }
  } catch (error) {
    // Best-effort only.
  }
};

const buildScopedStorageKey = (baseKey) => `${baseKey}:${getCurrentChatUserScope()}`;

export const loadChatSessions = () => {
  if (!canUseLocalStorage()) {
    return [];
  }

  try {
    const rawSessions = window.localStorage.getItem(buildScopedStorageKey(CHAT_SESSIONS_STORAGE_PREFIX));
    const parsedSessions = JSON.parse(rawSessions || '[]');
    return Array.isArray(parsedSessions) ? parsedSessions : [];
  } catch (error) {
    return [];
  }
};

export const saveChatSessions = (sessions = []) => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(buildScopedStorageKey(CHAT_SESSIONS_STORAGE_PREFIX), JSON.stringify(sessions));
  } catch (error) {
    // Keep chat usable even when browser storage is unavailable or full.
  }
};

export const loadActiveChatSessionId = () => {
  if (!canUseLocalStorage()) {
    return null;
  }

  try {
    return window.localStorage.getItem(buildScopedStorageKey(ACTIVE_CHAT_SESSION_STORAGE_PREFIX));
  } catch (error) {
    return null;
  }
};

export const saveActiveChatSessionId = (sessionId) => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    if (sessionId) {
      window.localStorage.setItem(buildScopedStorageKey(ACTIVE_CHAT_SESSION_STORAGE_PREFIX), sessionId);
    } else {
      window.localStorage.removeItem(buildScopedStorageKey(ACTIVE_CHAT_SESSION_STORAGE_PREFIX));
    }
  } catch (error) {
    // Storage persistence is best-effort; the live chat state remains intact.
  }
};

export const clearChatSessionStorage = () => {
  if (canUseLocalStorage()) {
    try {
      const keysToRemove = [];
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (
          key?.startsWith(CHAT_SESSIONS_STORAGE_PREFIX) ||
          key?.startsWith(ACTIVE_CHAT_SESSION_STORAGE_PREFIX)
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    } catch (error) {
      // Ignore storage cleanup failures.
    }
  }

  if (canUseSessionStorage()) {
    try {
      window.sessionStorage.removeItem(CHAT_USER_SCOPE_STORAGE_KEY);
      window.sessionStorage.removeItem(AI_BROWSER_SESSION_STORAGE_KEY);
      const keysToRemove = [];
      for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (
          key === DASHBOARD_SESSION_STORAGE_KEY ||
          key?.startsWith(`${DASHBOARD_SESSION_STORAGE_KEY}:`)
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
    } catch (error) {
      // Ignore storage cleanup failures.
    }
  }
};
